import React, {Component} from 'react';
import {RadioButton as RadioButtonCore} from 'wix-ui-core/radio-button';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {RenderContext, withRenderContext, type WithRenderContextProps} from '../../../../providers/renderContext';
import s from './SubscriptionPlan.scss';
import {SubscriptionPlanFrequency} from '../../../../../graphql/queries-schema';
import classNames from 'classnames';
import {getSubscriptionDetailsTranslation} from './translationUtils';
import {SPECS} from '../../../../specs';

export const enum SubscriptionPlanDataHooks {
  PLAN_ITEM = 'plan-item',
  NAME = 'plan-name',
  TAGLINE = 'plan-tagline',
  PRICE = 'plan-price',
  SELECTION = 'plan-selection',
  SELECTION_ICON = 'SELECTION_ICON',
  DETAILS = 'plan-details',
}

export interface ISubscriptionPlan {
  id?: string;
  name: string;
  isOneTimePurchase: boolean;
  formattedPrice: string;
  price?: number;
  tagline?: string;
  frequency?: SubscriptionPlanFrequency;
  duration?: number;
  interval?: number;
}

export interface ISubscriptionPlanProps extends ProvidedGlobalProps, IProvidedTranslationProps, WithRenderContextProps {
  plan: ISubscriptionPlan;
  planVariantFormattedPrice?: string;
  planFromFormattedPrice?: string;
  isSelected: boolean;
  accessibilityEnabled?: boolean;
  className?: string;
  onClick?(): void;
  hasError?: boolean;
  tabIndex?: number;
  shouldFocus?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

class SubscriptionPlanComponent extends Component<ISubscriptionPlanProps> {
  private readonly planItemRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate() {
    if (this.props.shouldFocus && this.planItemRef.current) {
      this.planItemRef.current.focus();
    }
  }

  private readonly onClick = () => {
    const {onClick: onCompClicked} = this.props;
    onCompClicked && onCompClicked();
  };

  private readonly getRadioButtonIcon = (checked: boolean) => {
    const circleClass = classNames(s.circle, {[s.checkedCircle]: checked});
    return <span data-hook={SubscriptionPlanDataHooks.SELECTION_ICON} className={circleClass} />;
  };

  private renderSubscriptionContent() {
    const {planVariantFormattedPrice, planFromFormattedPrice, t, globals, renderContext} = this.props;
    const {experiments, useExperiments} = globals;
    const {name, tagline, formattedPrice, frequency, interval} = this.props.plan;
    const isProductPageUpliftProductOptionsViewer = experiments?.productPageUpliftProductOptionsViewer;
    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;

    return (
      <div>
        <div
          data-hook={SubscriptionPlanDataHooks.NAME}
          className={classNames(s.name, {
            [s.upliftColor]: isProductPageUpliftProductOptionsViewer,
            [s.stickyDrawer]: isStickyDrawer,
          })}>
          {name}
        </div>
        {!!tagline && tagline.trim() && (
          <div
            className={classNames(s.subtitle, {
              [s.upliftColor]: isProductPageUpliftProductOptionsViewer,
              [s.stickyDrawer]: isStickyDrawer,
            })}
            data-hook={SubscriptionPlanDataHooks.TAGLINE}>
            {tagline}
          </div>
        )}
        <div>
          <span
            data-hook={SubscriptionPlanDataHooks.PRICE}
            className={classNames(s.price, {
              [s.upliftColor]: isProductPageUpliftProductOptionsViewer,
              [s.stickyDrawer]: isStickyDrawer,
            })}>
            {planFromFormattedPrice
              ? t('productPage.price.from.label', {formattedAmount: planFromFormattedPrice})
              : planVariantFormattedPrice || formattedPrice}
          </span>
          {!!frequency && !!interval && (
            <span
              data-hook={SubscriptionPlanDataHooks.DETAILS}
              className={classNames(s.planDetails, {
                [s.upliftColor]: isProductPageUpliftProductOptionsViewer,
                [s.stickyDrawer]: isStickyDrawer,
              })}>
              {getSubscriptionDetailsTranslation({t, plan: this.props.plan})}
            </span>
          )}
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    const {
      isSelected,
      className,
      hasError,
      tabIndex,
      onKeyDown,
      renderContext,
      globals: {product, experiments, isQuickView, useExperiments},
    } = this.props;
    const {name} = this.props.plan;
    const isProductPageUpliftProductOptionsViewer = experiments?.productPageUpliftProductOptionsViewer;
    const isSubscriptionNewDesign = experiments?.subscriptionPlansNewDesignViewer && !isQuickView;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;

    const subscriptionPlanClassesOld = classNames(s['old-container'], className, {
      [s.selected]: isSelected,
      [s.error]: hasError,
      [s.upliftFont]: isProductPageUpliftProductOptionsViewer,
      [s.subscriptionNewDesign]: isSubscriptionNewDesign,
      [s.stickyDrawer]: isStickyDrawer,
    });
    return (
      <div
        ref={this.planItemRef}
        data-hook={SubscriptionPlanDataHooks.PLAN_ITEM}
        className={subscriptionPlanClassesOld}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onClick={this.onClick}>
        <span className={s.radioButton}>
          <RadioButtonCore
            data-hook={SubscriptionPlanDataHooks.SELECTION}
            checked={isSelected}
            checkedIcon={this.getRadioButtonIcon(true)}
            uncheckedIcon={this.getRadioButtonIcon(false)}
            aria-label={name}
            name={product.id}
            required
            tabIndex={-1}
          />
        </span>
        {this.renderSubscriptionContent()}
      </div>
    );
  }
}

export const SubscriptionPlan = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(SubscriptionPlanComponent))
);
